import { type ReactNode, useEffect, useRef, useState } from 'react';
import { RANGES, zoneLabel, type Zone } from '@/lib/format';
import { clampRange, formatStamp, parseStamp, rangeLabel, resolveRange, type TimeRange } from '@/lib/time';
import { Icon } from './Icon';

export interface RangePickerProps {
    range: TimeRange;
    anchor: number;
    zone: Zone;
    open: boolean;
    onOpen: (open: boolean) => void;
    onRange: (range: TimeRange) => void;
}

// The time range: presets that follow the clock, or two fixed ends typed in Rome's time (or UTC's, as the list shows).
export function RangePicker(props: RangePickerProps): ReactNode {
    const root = useRef<HTMLDivElement>(null);
    const { open, onOpen } = props;

    useEffect(() => {
        if (!open) {
            return;
        }
        const close = (event: MouseEvent): void => {
            if (!root.current?.contains(event.target as Node)) {
                onOpen(false);
            }
        };
        window.addEventListener('mousedown', close);

        return () => window.removeEventListener('mousedown', close);
    }, [open, onOpen]);

    return (
        <div className="range" ref={root}>
            <button className={`ghost range-btn ${open ? 'on' : ''}`} onClick={() => onOpen(!open)} title="Time range (T)">
                <Icon name="clock" size={16} />
                {rangeLabel(props.range, props.zone)}
                <Icon name="chevron" size={14} />
            </button>
            {open && <RangePopover {...props} />}
        </div>
    );
}

// Mounted when the picker opens: the fixed ends start as the range on screen, then are the reader's to type.
function RangePopover(props: RangePickerProps): ReactNode {
    const { zone, onOpen } = props;
    const [fromText, setFromText] = useState(() => formatStamp(resolveRange(props.range, props.anchor).from, zone));
    const [toText, setToText] = useState(() => formatStamp(resolveRange(props.range, props.anchor).to, zone));
    const start = parseStamp(fromText, zone);
    const end = parseStamp(toText, zone);
    const valid = start !== null && end !== null && end > start;

    return (
        <div className="range-pop float">
            <div className="rp-title">Follow the clock</div>
            <div className="presets">
                {RANGES.map((preset) => (
                    <button
                        key={preset.key}
                        className={props.range.kind === 'preset' && props.range.key === preset.key ? 'on' : ''}
                        onClick={() => {
                            props.onRange({ kind: 'preset', key: preset.key });
                            onOpen(false);
                        }}
                    >
                        {preset.short}
                    </button>
                ))}
            </div>
            <div className="rp-title">
                Fixed ends <b>· {zoneLabel(zone)} time</b>
            </div>
            <form
                className="custom"
                onSubmit={(event) => {
                    event.preventDefault();
                    if (start !== null && end !== null && end > start) {
                        props.onRange({ kind: 'custom', ...clampRange(start, end) });
                        onOpen(false);
                    }
                }}
            >
                <label>
                    <span className="micro">From</span>
                    <input value={fromText} onChange={(event) => setFromText(event.target.value)} className={start === null ? 'bad' : ''} />
                </label>
                <label>
                    <span className="micro">To</span>
                    <input value={toText} onChange={(event) => setToText(event.target.value)} className={end === null ? 'bad' : ''} />
                </label>
                <button className="act primary" disabled={!valid} type="submit">
                    Apply
                </button>
            </form>
            <div className="rp-hint">
                Drag across the histogram to zoom in · <span className="kbd">[</span> <span className="kbd">]</span> zoom out and in
            </div>
        </div>
    );
}
